import dialoguesJson from "./generated/dialogues.json";

export type DialogueTrigger = "battleStart" | "firstRebirth" | "w1BossEncounter" | "w1BossDefeated";
export type DialogueTab = "battle";
export type DialogueExecutionType = "button" | "force";

export interface DialogueLine {
  readonly dialogueId: string;
  readonly order: number;
  readonly trigger: DialogueTrigger;
  readonly tab: DialogueTab;
  readonly executionType: DialogueExecutionType;
  readonly speakerKey: string;
  readonly textKey: string;
  readonly once: boolean;
}

export type RawDialogueRow = (typeof dialoguesJson)[number];

const TRIGGERS = new Set<DialogueTrigger>(["battleStart", "firstRebirth", "w1BossEncounter", "w1BossDefeated"]);
const TABS = new Set<DialogueTab>(["battle"]);
const EXECUTION_TYPES = new Set<DialogueExecutionType>(["button", "force"]);

export function parseDialogueLines(rows: readonly RawDialogueRow[]): DialogueLine[] {
  return rows
    .filter((row) => row.enabled)
    .map((row) => {
      if (!TRIGGERS.has(row.trigger as DialogueTrigger)) {
        throw new Error(`[dialogueData] unknown trigger: ${row.trigger}`);
      }
      if (!TABS.has(row.tab as DialogueTab)) {
        throw new Error(`[dialogueData] unknown tab: ${row.tab}`);
      }
      const executionType = (row as RawDialogueRow & { readonly executionType?: string }).executionType ?? "button";
      if (!EXECUTION_TYPES.has(executionType as DialogueExecutionType)) {
        throw new Error(`[dialogueData] unknown executionType: ${executionType}`);
      }
      if (!row.dialogueId.trim()) throw new Error("[dialogueData] dialogueId is required");
      if (!row.speakerKey.trim()) {
        throw new Error(`[dialogueData] speakerKey is required for ${row.dialogueId}`);
      }
      if (!row.textKey.trim()) {
        throw new Error(`[dialogueData] textKey is required for ${row.dialogueId}`);
      }
      return {
        dialogueId: row.dialogueId,
        order: Math.max(0, Math.floor(row.order)),
        trigger: row.trigger as DialogueTrigger,
        tab: row.tab as DialogueTab,
        executionType: executionType as DialogueExecutionType,
        speakerKey: row.speakerKey,
        textKey: row.textKey,
        once: row.once,
      };
    })
    .sort((a, b) => a.dialogueId.localeCompare(b.dialogueId) || a.order - b.order);
}

export const DIALOGUE_LINES: readonly DialogueLine[] = parseDialogueLines(dialoguesJson);

export function dialogueIdsForTrigger(
  trigger: DialogueTrigger,
  tab: DialogueTab,
  lines: readonly DialogueLine[] = DIALOGUE_LINES,
  executionType?: DialogueExecutionType,
): string[] {
  const ids: string[] = [];
  for (const line of lines) {
    if (line.trigger !== trigger || line.tab !== tab) continue;
    if (executionType && line.executionType !== executionType) continue;
    if (!ids.includes(line.dialogueId)) ids.push(line.dialogueId);
  }
  return ids;
}

export function dialogueLinesById(
  dialogueId: string,
  lines: readonly DialogueLine[] = DIALOGUE_LINES,
): DialogueLine[] {
  return lines.filter((line) => line.dialogueId === dialogueId).sort((a, b) => a.order - b.order);
}

export function allDialogueIds(lines: readonly DialogueLine[] = DIALOGUE_LINES): string[] {
  const ids: string[] = [];
  for (const line of lines) {
    if (!ids.includes(line.dialogueId)) ids.push(line.dialogueId);
  }
  return ids;
}
